import { useEffect, useRef } from "react";

import type {
  ArmyDeploymentTargetView,
  ArmyHandDetailsView,
} from "../presentation/game-view-model";
import { formatResources } from "../presentation/resource-format";

interface ArmyDeploymentPanelProps {
  armyName: string;
  army: ArmyHandDetailsView;
  target: ArmyDeploymentTargetView;
  validationMessage: string | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ArmyDeploymentPanel({
  armyName,
  army,
  target,
  validationMessage,
  onConfirm,
  onCancel,
}: ArmyDeploymentPanelProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const projectedSustainable = target.projectedNextSupplySustainable !== false;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (bodyRef.current !== null) bodyRef.current.scrollTop = 0;
      titleRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [armyName, target.laneId]);

  return (
    <section
      className="commander-deployment army-deployment"
      aria-labelledby="army-deployment-title"
      data-testid="army-deployment-panel"
      data-projected-supply={projectedSustainable ? "sustainable" : "shortfall"}
    >
      <header className="commander-deployment__header army-deployment__header">
        <div>
          <span className="eyebrow">Army Deployment</span>
          <h2 id="army-deployment-title" ref={titleRef} tabIndex={-1}>
            Deploy {armyName} to {target.label} lane?
          </h2>
        </div>
        <div className="commander-deployment__badges" aria-label="Deployment consequences">
          <span>1 Deployment</span>
          {!projectedSustainable ? <span>Next Supply warning</span> : null}
        </div>
      </header>

      <dl className="commander-deployment__summary" aria-label="Army deployment summary">
        <div>
          <dt>Cost</dt>
          <dd>{formatResources(army.cost)}</dd>
        </div>
        <div>
          <dt>Army cards</dt>
          <dd>
            {target.armyCountAfter ?? target.armyCountBefore} / {target.maximumArmyCount}
          </dd>
        </div>
        <div>
          <dt>Army Size</dt>
          <dd>
            {target.totalArmySizeAfter ?? target.totalArmySizeBefore} /{" "}
            {target.commanderCapacity ?? "—"}
          </dd>
        </div>
      </dl>

      <div className="commander-deployment__body" ref={bodyRef}>
        <dl className="commander-deployment__stats" aria-label={`${armyName} printed values`}>
          <div>
            <dt>Attack</dt>
            <dd>{army.attack}</dd>
          </div>
          <div>
            <dt>Guard</dt>
            <dd>{army.guard}</dd>
          </div>
          <div>
            <dt>Upkeep</dt>
            <dd>{formatResources(army.upkeep)}</dd>
          </div>
        </dl>

        <p className="commander-deployment__consequence" data-testid="army-combat-consequence">
          Pool after payment:{" "}
          <strong>{formatResources(target.kingdomPoolAfter ?? target.kingdomPoolBefore)}</strong>.
          This Army contributes its printed Attack and Guard immediately. It does not ready its
          Commander or grant another attack.
        </p>

        <p
          className={
            projectedSustainable
              ? "commander-deployment__authorization"
              : "commander-deployment__authorization army-deployment__warning"
          }
          role="status"
          data-testid="army-next-supply-preview"
        >
          Next Supply: Land production {formatResources(target.laneProduction)}; projected Upkeep{" "}
          {formatResources(target.totalUpkeepAfter ?? target.totalUpkeepBefore)}.
          {projectedSustainable
            ? " This lane is projected to remain sustainable."
            : ` This lane is projected to be unsustainable (shortfall ${formatResources(target.shortfallAfter ?? target.totalUpkeepBefore)}). Deployment is legal now; you may need to discard an Army next Supply.`}
        </p>
      </div>

      {validationMessage ? (
        <p className="commander-deployment__validation" role="status">
          {validationMessage}
        </p>
      ) : null}

      <div className="commander-deployment__actions">
        <button className="action-button" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="action-button action-button--primary"
          type="button"
          data-testid="confirm-army-deployment"
          onClick={onConfirm}
        >
          Deploy Army
        </button>
      </div>
    </section>
  );
}
